import React, { useEffect, useState } from 'react';

import * as sanitizer from '@amzn/aws-console-html-sanitizer';
import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Input from '@amzn/meridian/input';
import Loader from '@amzn/meridian/loader';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import { useQuery } from '@tanstack/react-query';
import Handlebars from 'handlebars';
import useForm from 'src/hooks/useForm';
import useIsT2Experience from 'src/hooks/useIsT2Experience';
import { getAccommodationQueryOptions } from 'src/services/accommodation/accommodationQueries';
import {
  createAccommodation,
  getEmailSettingsForSite,
  setApprovalFormData,
  setApprovalFormErrors,
  setIsCancelModalOpen,
  setPageProgress,
} from 'src/store/newJmrPageSlice/newJmrPageSlice';
import { type ApprovalFormData } from 'src/store/newJmrPageSlice/types';
import { useAppDispatch, useAppSelector } from 'src/store/store';
import { ApiCallStatus } from 'src/store/type';
import { isEmailValid } from 'src/utils/helpers';
import {
  CaseSources,
  getGroupedModifications,
  getRestrictionsForModification,
  JmrPageProgress,
} from 'src/utils/jmrUtils';
import { getModifiedShiftLength } from 'src/utils/placementSelectionUtils';

import { NewJMRPageFooter, NewJMRPageMainBody, NewJMRPageWrapper } from '../pageLayout/pageLayout';

Handlebars.registerHelper('numberFormat', (value) =>
  Number(value).toLocaleString('en-US', { maximumFractionDigits: 2, useGrouping: false }),
);

/**
 * Default component
 */
const SendForApprovalPage = () => {
  const dispatch = useAppDispatch();
  const {
    backgroundFormData,
    placementId,
    approvalFormData,
    approvalFormErrors,
    accommodationSavingStatus,
    selectedLimitations,
    selectedTasks,
    jmrResults,
    decisionHubLink,
  } = useAppSelector((state) => state.newJmrPage);
  const { alias, featureFlags } = useAppSelector((state) => state.user);
  const [emailContent, setEmailContent] = useState('');
  const containsT2ModifiedJobs = useIsT2Experience() && jmrResults.find((r) => r.tierTwoPlusJobModified);
  const isNPLEnabled = backgroundFormData.site?.nonPhysicalLimitationsEnabled && featureFlags.NON_PHYSICAL_LIMITATIONS;

  // Setup form hooks
  const { validateForm, setFormValues, errors } = useForm<ApprovalFormData>({
    validations: {
      emailRecipients: {
        custom: {
          isValid: (value) => isEmailValid(value),
          message: 'Must be valid email',
        },
      },
    },
  });

  const emailTemplateQuery = useQuery(getAccommodationQueryOptions.emailTemplate());

  const handleChange = (key: keyof ApprovalFormData, value: any) => {
    dispatch(setApprovalFormData({ ...approvalFormData, [key]: value }));
    dispatch(setApprovalFormErrors({ ...approvalFormErrors, [key]: undefined }));
  };

  const getT2ModificationsList = () => {
    const modifiedJobs = jmrResults
      .filter((job) => job.isChecked && job.tierTwoPlusJobModified)
      .map((job) => ({ name: job.modificationNotes, details: [] }));

    if (modifiedJobs.length) {
      return modifiedJobs;
    }
  };

  const getT1ModificationsList = () => {
    const modifications = getGroupedModifications(jmrResults).map((entry) => ({
      name: entry.name + (entry.freeFormText ? `: ${entry.freeFormText}` : ''),
      details: entry.conflicts.map(getRestrictionsForModification),
    }));

    return modifications;
  };

  useEffect(() => {
    if (approvalFormData) {
      setFormValues(approvalFormData);
    }
  }, [approvalFormData]);

  useEffect(() => {
    if (emailTemplateQuery.isSuccess) {
      setEmailContent(
        sanitizer.sanitizeHtmlString(
          Handlebars.compile(emailTemplateQuery.data)({
            title: '',
            associateFullName: backgroundFormData.fullName,
            restrictedHours: getModifiedShiftLength(
              backgroundFormData.shiftLength,
              backgroundFormData.limitedHoursPerDay,
            ),
            jobs: jmrResults.filter((r) => r.isChecked).map((j) => j.name),
            modifications: containsT2ModifiedJobs ? getT2ModificationsList() : getT1ModificationsList(),
            requester: alias,
            restrictions: selectedTasks.map((r) => r.label),
            limitations: selectedLimitations,
            hasLimitations: selectedLimitations.length > 0 && isNPLEnabled,
            siteName: backgroundFormData.site?.site,
            associateLogin: backgroundFormData.alias,
            employeeId: backgroundFormData.employeeID,
            austinCaseLink: backgroundFormData.caseSource === CaseSources.Austin ? backgroundFormData.caseID : 'N/A',
            decisionHubLink: decisionHubLink,
            isUpdate: !!placementId,
          }),
          { keepFormattingTags: true },
        ),
      );
    }
  }, [emailTemplateQuery.isSuccess]);

  useEffect(() => {
    dispatch(setApprovalFormErrors(errors));
  }, [dispatch, errors]);

  useEffect(() => {
    dispatch(getEmailSettingsForSite());
  }, [dispatch]);

  return (
    <NewJMRPageWrapper>
      {/* Main Body */}
      <NewJMRPageMainBody title="Approval Notification" xs="85%" md="75%" lg="50%">
        <Text type="b300">
          Choose who to send this Job Placement to for approval. Content in this email cannot be edited. Note that this
          information will also send a webhook if setup. An email will be sent to you and anyone you specify below
        </Text>
        <Column spacingInset="400 none 600 none" spacing="300">
          {/* Email Input */}
          <Text type="b300">Email Recipients</Text>
          <Text type="b200" color="secondary">
            Please enter email addresses separated by a semi-colon. Addresses must include @amazon.com.
          </Text>
          <Input
            data-cy="new-jmr__approval__email"
            aria-label="Approval Email"
            placeholder="e.g. jasrulo@amazon.com"
            value={approvalFormData.emailRecipients}
            onChange={(emailRecipients) => handleChange('emailRecipients', emailRecipients)}
            error={approvalFormErrors.emailRecipients?.error}
            errorMessage={approvalFormErrors.emailRecipients?.errorMessage}
          />

          {/* Email Preview */}
          <Box type="outline" spacingInset="400">
            <Text className="newJmr__approval-email">
              <span dangerouslySetInnerHTML={{ __html: emailContent }}></span>
            </Text>
          </Box>
        </Column>
      </NewJMRPageMainBody>

      {/* Footer */}
      <NewJMRPageFooter>
        <Row spacing="400">
          <div></div>
          <Button type="link" onClick={() => dispatch(setIsCancelModalOpen(true))}>
            Cancel
          </Button>
          <Button type="tertiary" onClick={() => dispatch(setPageProgress(JmrPageProgress.Review))}>
            Previous
          </Button>
          <Button
            data-cy="new-jmr__approval__next"
            disabled={accommodationSavingStatus === ApiCallStatus.Pending}
            onClick={() => (validateForm() ? dispatch(createAccommodation()) : null)}
          >
            {accommodationSavingStatus === ApiCallStatus.Pending ? <Loader size="small" /> : 'Send for Approval'}
          </Button>
        </Row>
      </NewJMRPageFooter>
    </NewJMRPageWrapper>
  );
};

export default SendForApprovalPage;
